<template>
	<div>
		<PageHeader
			:data="{
				name: 'Departments',
				breadcrumbs: [{ name: 'Home', to: '/' }],
			}"
		/>

		<div v-if="!error" class="row mb-3">
			<!-- Filters -->
			<div class="col-12 mb-2">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center">
							<div class="header d-flex align-items-center">
								<h4 class="p-0 m-0 me-2">
									<font-awesome-icon :icon="['fas', 'filter']" class="text-primary me-1" /> Filters
								</h4>
								<button
									v-if="Object.values(this.params).length > 4 && searchActive === ''"
									@click="clearFilters()"
									class="btn btn-sm btn-danger text-white me-2"
								>
									Clear
									<font-awesome-icon :icon="['fas', 'times']" />
								</button>
								<button
									v-if="!searchSaving && haveParamsToSave() && searchActive === ''"
									@click="searchSaving = true"
									class="btn btn-sm btn-primary"
								>
									Save search
								</button>
								<div v-if="searchSaving && searchActive === ''" class="save-search d-flex">
									<form @submit.prevent="storeSearch" class="d-flex">
										<input
											type="text"
											v-model="searchName"
											class="form-control form-control-sm ms-2"
											placeholder="enter search name"
											required
										/>
										<button type="submit" class="btn btn-sm btn-primary ms-1">
											Save
										</button>
										<a role="button" @click="searchSaving = false" class="btn btn-sm ms-1">
											Cancel
										</a>
									</form>
								</div>
								<span v-if="searchActive !== ''" class="badge bg-success"
									>{{ searchActive }}
									<font-awesome-icon
										role="button"
										@click="clearFilters()"
										class="ms-2"
										:icon="['fas', 'times']"
								/></span>
							</div>
							<div class="action-buttons">
								<button
									data-bs-toggle="offcanvas"
									data-bs-target="#offcanvasSearches"
									aria-controls="offcanvasSearches"
									class="btn btn-outline-secondary btn-sm me-2"
								>
									Saved Searches <font-awesome-icon :icon="['fas', 'save']" class="me-1" />
								</button>
							</div>
						</div>

						<LPDataTableFilters :filters="table.filters" v-on:getFilters="getFilters($event.filters)" />
					</div>
				</div>
			</div>
			<!-- Table data -->
			<div class="col-12 mb-2">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center">
							<div class="header d-flex align-items-center">
								<h4 class="p-0 m-0 me-2 text-capitalize">{{ model }}</h4>
								<a
									v-if="authUser.permissions.includes('MUNICIPALITY_CREATE')"
									data-bs-toggle="modal"
									data-bs-target="#create"
									class="btn btn-outline-primary btn-sm"
									>Add {{ model }} <font-awesome-icon :icon="['fas', 'plus-circle']" class="me-1"
								/></a>
							</div>

							<div class="action-buttons">
								<button
									data-bs-toggle="offcanvas"
									data-bs-target="#offcanvasTableSettings"
									aria-controls="offcanvasTableSettings"
									class="btn btn-outline-secondary btn-sm"
								>
									Table Settings <font-awesome-icon :icon="['fas', 'cogs']" class="me-1" />
								</button>
							</div>
						</div>
						<table
							:class="
								!loading && tableData.length > 0 ? 'table table-hover' : 'table table-hover loading'
							"
						>
							<LPDataTableThead
								:thead="table.columns"
								:userColumns="userColumns"
								v-on:setOrder="setOrderBy($event.orderBy, $event.direction)"
							/>
							<LPDataTableTbody
								:userColumns="userColumns"
								:authUser="authUser"
								:data="tableData"
								:tbody="table.columns"
								:model="model"
								:route="route"
								v-on:deleted="getTableData"
							/>
						</table>
						<div v-if="!loading && tableData.length === 0" class="col-md-12 text-center">
							<h4>No data available</h4>
						</div>
						<div v-if="loading && tableData.length === 0" class="d-flex justify-content-center">
							<div class="spinner-border" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
						</div>
						<div class="table-actions d-flex">
							<button
								data-bs-toggle="modal"
								data-bs-target="#exportModal"
								class="btn btn-success btn-sm me-2 fw-normal"
							>
								Export<font-awesome-icon :icon="['fas', 'file-csv']" class="ms-2" />
							</button>
							<router-link
								v-if="
									authUser.permissions.includes(model.toUpperCase() + '_IMPORT') &&
										authUser.email.includes('srdjan')
								"
								:to="`/${route}/import`"
								class="btn btn-primary btn-sm me-2 fw-normal"
							>
								Import <font-awesome-icon :icon="['fas', 'file-csv']" class="ms-2" />
							</router-link>
						</div>
						<LPDataTablePagination :pagination="pagination" v-on:getItems="getItems($event.page)" />
					</div>
				</div>
			</div>
		</div>

		<div v-else class="row mb-3">
			<div class="col-12">
				<div class="card">
					<div class="card-body">
						<div class="alert alert-danger" role="alert">
							{{ error }}
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal for adding Data -->
		<div class="modal fade" id="create" tabindex="-1" aria-labelledby="createLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="exampleModalLabel">Create Department</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="form-group mb-2">
							<label for="">Department Name</label>
							<input type="text" class="form-control" v-model="newDepartment.name" />
						</div>
						<div class="form-group mb-2">
							<label for="">Type</label>
							<select class="form-select" v-model="newDepartment.type">
								<option value="Police department">Police department</option>
								<option value="Fire department">Fire department</option>
							</select>
						</div>
						<!-- If type is municipality show municipality search -->
						<div class="mb-2">
							<MunicipalitySearch
								:selected="null"
								excludeClients="false"
								v-on:setMunicipality="setMunicipality"
							/>
						</div>
						<div class="form-group mb-2">
							<label for="">Website</label>
							<input type="text" class="form-control" v-model="newDepartment.website" />
						</div>
						<div class="form-group mb-2">
							<label for="">First Name</label>
							<input type="text" class="form-control" v-model="newDepartment.first_name" />
						</div>
						<div class="form-group mb-2">
							<label for="">Last Name</label>
							<input type="text" class="form-control" v-model="newDepartment.last_name" />
						</div>
						<div class="form-group mb-2">
							<label for="">Email</label>
							<input type="text" class="form-control" v-model="newDepartment.email" />
						</div>
						<div class="form-group mb-2">
							<label for="">Phone</label>
							<input type="text" class="form-control" v-model="newDepartment.phone" />
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
						<button type="submit" @click="createDepartment()" class="btn btn-primary">Create</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal for export -->
		<div class="modal fade" id="exportModal" tabindex="-1" aria-labelledby="exportModalLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="exampleModalLabel">Export {{ route }}</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="form-group mb-2">
							<label for="">Export Type</label>
							<select class="form-select" v-model="newExport.type">
								<option value="csv">CSV</option>
							</select>
						</div>
						<div class="form-group mb-2">
							<label for="">File Name</label>
							<div class="input-group mb-3">
								<input
									type="text"
									class="form-control"
									placeholder="File name"
									v-model="newExport.filename"
								/>
								<span class="input-group-text" id="basic-addon2">.{{ newExport.type }}</span>
							</div>
						</div>
						<div class="form-group">
							<label for="">Columns to export</label>
							<div class="form-check">
								<input
									class="form-check-input"
									type="radio"
									v-model="newExport.columnsType"
									value="visible"
									id="visibleColumnsRadio"
									checked
								/>
								<label class="form-check-label" for="visibleColumnsRadio">
									Export visible columns
								</label>
							</div>
							<div class="my-2" v-if="newExport.columnsType === 'visible'">
								<div class="d-flex flex-wrap">
									<div
										:key="'column-available-' + column"
										v-for="column in newExport.exportColumns"
										class="mb-2 me-1"
									>
										<span class="badge bg-success">{{ column }}</span>
									</div>
								</div>
							</div>
							<div class="form-check">
								<input
									class="form-check-input"
									type="radio"
									v-model="newExport.columnsType"
									value="custom"
									id="customColumnsRadio"
								/>
								<label class="form-check-label" for="customColumnsRadio">
									Export custom columns
								</label>
							</div>
							<div class="my-2" v-if="newExport.columnsType === 'custom'">
								<div class="d-flex flex-wrap">
									<small class="w-100 mb-1"
										>ℹ️ Click on column to include/exclude it from export</small
									>
									<div
										:key="'column-available-' + column"
										v-for="column in newExport.availableColumns"
										class="mb-2 me-1"
									>
										<span
											role="button"
											@click="toggleExportColumn(column)"
											v-if="newExport.exportColumns.includes(column)"
											class="badge bg-success"
											>{{ column }}</span
										>
										<span
											role="button"
											@click="toggleExportColumn(column)"
											v-else
											class="badge bg-secondary"
											>{{ column }}</span
										>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
						<button
							v-if="!loadExporting && exportingFile === ''"
							@click="exportData()"
							type="submit"
							class="btn btn-primary"
						>
							Create Export
						</button>
						<a
							v-if="!loadExporting && exportingFile !== ''"
							@click="downloadExport()"
							:href="exportingFile"
							class="btn btn-primary"
							download
						>
							Download
						</a>

						<button v-if="loadExporting" class="btn btn-primary" type="button" disabled>
							<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
							Loading...
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Offcanvas for Filters -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasFilters" aria-labelledby="offcanvasRightLabel">
			<div class="offcanvas-header">
				<h5 id="offcanvasRightLabel">Filters settings</h5>
				<button
					type="button"
					class="btn-close text-reset"
					data-bs-dismiss="offcanvas"
					aria-label="Close"
				></button>
			</div>
			<div class="offcanvas-body">
				Filters settings
			</div>
		</div>

		<!-- Offcanvas for Searches -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasSearches" aria-labelledby="offcanvasRightLabel">
			<div class="offcanvas-header">
				<h5 id="offcanvasRightLabel">Saved searches</h5>
				<button
					type="button"
					class="btn-close text-reset"
					data-bs-dismiss="offcanvas"
					aria-label="Close"
				></button>
			</div>
			<div class="offcanvas-body">
				<h4 class="m-0">Your saved searches</h4>
				<p class="p-0"><small>ℹ️ Click on search to apply search filters to table</small></p>
				<div class="table-columns-settings">
					<div class="d-flex flex-wrap">
						<div :key="'user-searches-' + search.id" v-for="search in searches" class="mb-2 me-1">
							<span
								data-bs-toggle="offcanvas"
								data-bs-target="#offcanvasSearches"
								aria-controls="offcanvasSearches"
								role="button"
								@click="applySearch(search)"
								class="badge bg-success"
								>{{ search.name }}</span
							>
						</div>
					</div>
				</div>

				<small
					>👨‍🏫 Your saved searches for fast applying filters, you can add important ones to display above table
					(🚧 coming soon)</small
				>
			</div>
		</div>

		<!-- Offcanvas for Table Settings -->
		<div
			class="offcanvas offcanvas-end"
			tabindex="-1"
			id="offcanvasTableSettings"
			aria-labelledby="offcanvasRightLabel"
		>
			<div class="offcanvas-header">
				<h5 id="offcanvasRightLabel" class="text-capitalize">{{ route }} settings</h5>
				<button
					type="button"
					class="btn-close text-reset"
					data-bs-dismiss="offcanvas"
					aria-label="Close"
				></button>
			</div>
			<div class="offcanvas-body">
				<h4 class="m-0">Display columns</h4>
				<p class="p-0"><small>ℹ️ Select which columns to display in table</small></p>
				<div class="table-columns-settings">
					<div class="d-flex flex-wrap">
						<div :key="'column-available-' + column" v-for="column in availableColumns" class="mb-2 me-1">
							<span
								role="button"
								@click="toggleUserColumn(column)"
								v-if="userColumns.includes(column)"
								class="badge bg-success"
								>{{ column }}</span
							>
							<span role="button" @click="toggleUserColumn(column)" v-else class="badge bg-secondary">{{
								column
							}}</span>
						</div>
					</div>
				</div>

				<small
					>👨‍🏫 Selected columns are automatically saved to your browser storage and will be used again when you
					come on this page</small
				>

				<h4 class="mt-3">Display {{ this.route }} per page</h4>
				<p class="p-0">
					<small>ℹ️ Select how many {{ this.route }} to display per page in table</small>
				</p>
				<div class="form-group w-75">
					<select v-model="userPerPage" @change="setUserPerPage()" class="form-select">
						<option value="10">10 per page</option>
						<option value="15">15 per page</option>
						<option value="20">20 per page</option>
						<option value="30">30 per page</option>
						<option value="50">50 per page</option>
						<option value="100">100 per page</option>
					</select>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import { mapState } from 'vuex'
import restApi from '../../api/index.js'
import { Modal } from 'bootstrap'
import { handleErrors } from '../../actions/helpers'
import LPDataTablePagination from '@/components/lp-data-table/LPDataTablePagination'
import LPDataTableFilters from '@/components/lp-data-table/LPDataTableFilters'
import LPDataTableThead from '@/components/lp-data-table/LPDataTableThead'
import LPDataTableTbody from '@/components/lp-data-table/LPDataTableTbody'
import MunicipalitySearch from '@/components/municipality/MunicipalitySearch'

export default {
	name: 'Departments',
	components: {
		PageHeader,
		LPDataTablePagination,
		LPDataTableFilters,
		LPDataTableThead,
		LPDataTableTbody,
		MunicipalitySearch,
	},
	data() {
		return {
			route: 'departments',
			model: 'department',
			tableData: [],
			loading: false,
			error: '',
			newTableEntry: {},
			$createModal: null,
			$exportModal: null,
			pagination: {},
			page: this.$route.query.page || 1,
			orderBy: this.$route.query.orderBy || 'departments.id',
			direction: this.$route.query.direction || 'asc',
			userPerPage: localStorage.getItem('department-per-page') || 20,
			table: {
				columns: [
					{
						name: 'id',
						label: 'ID',
						sort: true,
						type: 'default',
					},
					{
						name: 'name',
						label: 'Department name',
						sort: true,
						type: 'default',
					},
					{
						name: 'municipality_name',
						label: 'Municipality',
						sort: true,
						type: 'default',
					},
					{
						name: 'type',
						label: 'Type',
						sort: true,
						type: 'default',
					},
					{
						name: 'website',
						label: 'Website',
						sort: false,
						type: 'default',
					},
					{
						name: 'first_name',
						label: 'First Name',
						sort: false,
						type: 'default',
					},
					{
						name: 'last_name',
						label: 'Last Name',
						sort: false,
						type: 'default',
					},
					{
						name: 'email',
						label: 'Email',
						sort: false,
						type: 'default',
					},
					{
						name: 'phone',
						label: 'Phone',
						sort: false,
						type: 'default',
					},
					{
						name: 'created_at',
						label: 'Created',
						sort: false,
						type: 'default',
					},
					{
						name: 'updated_at',
						label: 'Updated',
						sort: false,
						type: 'default',
					},
					{
						name: 'actions',
						label: 'Actions',
						sort: false,
						type: 'actions',
						width: '100px',
						buttons: [
							{
								type: 'default',
								label: 'Edit',
								action: '',
								class: 'btn-primary',
								permission: `CONTACT_UPDATE`,
							},
							{
								type: 'delete',
								label: 'Delete',
								action: '',
								class: 'btn-danger',
								permission: `CLIENT_DELETE`,
							},
						],
					},
				],
				filters: [
					{
						display: true,
						name: 'search',
						label: 'Search',
						type: 'text',
						placeholder: 'enter text and press enter',
						colClass: 'col-3',
						value: this.$route.query.search || '',
					},
					{
						display: true,
						name: 'type',
						label: 'Type',
						type: 'select',
						placeholder: 'All types',
						colClass: 'col-2',
						value: this.$route.query.type || '',
						options: [
							{
								id: 'Police department',
								name: 'Police department',
							},
							{
								id: 'Fire department',
								name: 'Fire department',
							},
						],
					},
					{
						display: true,
						name: 'state',
						label: 'State',
						type: 'multiselect',
						placeholder: 'All states',
						colClass: 'col-3',
						options: [],
						optionValue: true,
						optionsEndpoint: `municipalities/states`,
						value: this.$route.query.state || [],
					},
					{
						display: true,
						name: 'county',
						label: 'County',
						type: 'multiselect',
						placeholder: 'All counties',
						colClass: 'col-4',
						options: [],
						value: this.$route.query.county || [],
					},
				],
			},
			params: {},
			userColumns: JSON.parse(localStorage.getItem(`department-columns`)) || [
				'id',
				'name',
				'type',
				'website',
				'first_name',
				'last_name',
				'email',
				'actions',
			],
			availableColumns: ['id'],
			searchName: '',
			searchSaving: false,
			searchActive: '',
			searches: [],
			newExport: {
				type: 'csv',
				columnsType: 'visible',
				filename: '',
				availableColumns: ['id'],
				exportColumns: JSON.parse(localStorage.getItem(`department-columns`)) || [
					'id',
					'name',
					'type',
					'website',
					'first_name',
					'last_name',
					'email',
					'actions',
				],
			},
			loadExporting: false,
			exportingFile: '',
			toSync: 1,
			newDepartment: {
				name: '',
				municipality_id: '',
				type: 'Police department',
				website: '',
				first_name: '',
				last_name: '',
				email: '',
				phone: '',
			},
		}
	},
	computed: {
		...mapState(['authUser', 'storageUrl']),
	},
	created() {
		this.table.filters.forEach(filter => {
			if (filter.value && filter.value !== '' && filter.value.length > 0) {
				this.params[filter.name] = filter.value
			} else {
				delete this.params[filter.name]
			}

			if (filter.from && filter.from !== '' && filter.to && filter.to !== '') {
				this.params['from' + filter.name] = filter.from
				this.params['to' + filter.name] = filter.to
			} else {
				delete this.params['from' + filter.name]
				delete this.params['to' + filter.name]
			}
		})
		this.getCountyOptions()
		this.getTableColumns()
		this.getTableData(true)
		this.getSearches()
	},
	mounted() {
		this.$createModal = new Modal('#create')
		this.$exportModal = new Modal('#exportModal')
	},
	methods: {
		// Get table Columns
		getTableColumns() {
			restApi
				.post('/helper/table-columns', { table: this.route })
				.then(({ data }) => {
					this.newExport.availableColumns = data.map(column => column.name)
					this.newExport.availableColumns.push('municipality_name')
					this.availableColumns = data.map(column => column.name)
					this.availableColumns.push('municipality_name')
					this.availableColumns.push('actions')
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},
		// Get table data from API
		getTableData(first = false) {
			this.loading = true

			this.params = {
				...this.params,
				page: this.page,
				orderBy: this.orderBy,
				direction: this.direction,
				perPage: localStorage.getItem(`${this.model}-per-page`) || 20,
			}

			restApi
				.get(`/${this.route}`, { params: this.params })
				.then(({ data }) => {
					this.tableData = data.data
					this.pagination = data
					delete this.pagination.data
					this.loading = false
					if (!first) {
						this.$router.push({ path: this.$router.currentPath, query: this.params })
					}
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Adding new filter
		addFilter() {
			this.$addFilterModal.hide()
		},

		// Go to user page
		viewUser(user) {
			this.$router.push('/users/' + user.id)
		},

		// Get items from page that returned from LPDataTablePagination
		getItems(page) {
			this.page = page
			this.getTableData()
		},

		// Set direction
		setOrderBy(orderBy, direction) {
			this.orderBy = orderBy
			this.direction = direction
			this.getTableData()
		},

		// Get filters from LPDataTableFilters component
		getFilters(filters) {
			this.page = ''
			this.searchActive = ''
			filters.forEach(filter => {
				if (filter.value && filter.value !== '' && filter.value.length > 0) {
					this.params[filter.name] = filter.value
				} else {
					delete this.params[filter.name]
				}

				// Setting filter if its type is range
				if (filter.from && filter.from !== '' && filter.to && filter.to !== '') {
					this.params['from' + filter.name] = filter.from
					this.params['to' + filter.name] = filter.to
				} else {
					delete this.params['from' + filter.name]
					delete this.params['to' + filter.name]
				}
			})
			this.getCountyOptions()
			this.getTableData()
		},

		// Toggle user column
		toggleUserColumn(column) {
			if (this.userColumns.includes(column)) {
				this.userColumns = this.userColumns.filter(c => c !== column)
			} else {
				this.userColumns.push(column)
			}
			this.updateUserColumns()
		},

		// Update user columns
		updateUserColumns() {
			localStorage.setItem(`${this.model}-columns`, JSON.stringify(this.userColumns))
		},

		// Get user saved searches
		getSearches() {
			let params = {
				user_id: this.authUser.id,
				model: this.model,
			}
			restApi
				.get('/searches', { params })
				.then(({ data }) => {
					data.forEach(d => {
						d.data = JSON.parse(d.data)
						return d
					})
					this.searches = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Enable saving if some filters are set
		haveParamsToSave() {
			if (Object.values(this.params).length > 4) {
				return true
			} else {
				return false
			}
		},

		// Get user saved searches
		storeSearch() {
			let dataParams = this.params
			restApi
				.post('/searches', {
					user_id: this.authUser.id,
					model: `${this.model}`,
					name: this.searchName,
					data: JSON.stringify(dataParams),
				})
				.then(({ data }) => {
					data.data = JSON.parse(data.data)
					this.searches.push(data)
					this.searchActive = this.searchName
					this.searchSaving = false
					this.$swal.fire({
						icon: 'success',
						title: 'Search successfully saved!',
						timer: 4000,
					})
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Apply saved search to table
		applySearch(search) {
			// Reset all before applying saved search
			this.params = {}
			this.table.filters.forEach(filter => {
				if (filter.type !== 'range' && filter.type !== 'daterange') {
					filter.value = ''
				} else {
					filter.from = ''
					filter.to = ''
				}
			})
			// Apply saved search
			this.params = search.data
			let paramsArray = Object.keys(search.data)
			this.table.filters.forEach(filter => {
				if (paramsArray.includes(filter.name) && filter.type !== 'range') {
					filter.value = this.params[filter.name]
				}
				if (paramsArray.includes('from' + filter.name) && filter.type === 'range') {
					filter.from = this.params['from' + filter.name]
					filter.to = this.params['to' + filter.name]
				}
				if (paramsArray.includes('from' + filter.name) && filter.type === 'daterange') {
					filter.from = this.params['from' + filter.name]
					filter.to = this.params['to' + filter.name]
				}
			})
			this.searchActive = search.name
			this.getTableData()
		},

		// Clear all filters
		clearFilters() {
			this.params = {}
			this.searchActive = ''
			this.table.filters.forEach(filter => {
				if (filter.type !== 'range' && filter.type !== 'multiselect') {
					filter.value = ''
				}
				if (filter.type === 'multiselect') {
					filter.value = []
				}
				if (filter.type === 'range' || filter.type === 'daterange') {
					filter.from = ''
					filter.to = ''
				}
			})
			this.getCountyOptions()
			this.getTableData()
		},

		// Toggle export column
		toggleExportColumn(column) {
			if (this.newExport.exportColumns.includes(column)) {
				this.newExport.exportColumns = this.newExport.exportColumns.filter(c => c !== column)
			} else {
				this.newExport.exportColumns.push(column)
			}
		},

		// Export table Data
		exportData() {
			this.loadExporting = true
			this.newExport.exportColumns = this.newExport.exportColumns.filter(column => column !== 'actions')
			restApi
				.post(`/${this.route}/export`, {
					params: JSON.stringify(this.params),
					columns: JSON.stringify(this.newExport.exportColumns),
					filename: this.newExport.filename,
				})
				.then(({ data }) => {
					this.loadExporting = false
					this.exportingFile = this.storageUrl + '/tmp/' + data.file
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Download exported Data
		downloadExport() {
			this.$exportModal.hide()
			this.newExport.filename = ''
			this.exportingFile = ''
		},

		// Set user per page
		setUserPerPage() {
			localStorage.setItem(`${this.model}-per-page`, this.userPerPage)
			this.getTableData()
		},

		// Get county options for selected state
		getCountyOptions() {
			if (this.params.state) {
				restApi.post('municipalities/counties', { state: this.params.state }).then(({ data }) => {
					data = data.filter(county => county !== null)
					this.counties = data
					let filter = this.table.filters.find(f => f.name === 'county')
					filter.options = data
				})
			} else {
				let filter = this.table.filters.find(f => f.name === 'county')
				filter.options = []
				filter.disabled = true
			}
		},

		setMunicipality(municipality) {
			if (municipality !== null) {
				this.newDepartment.municipality_id = municipality.id
			} else {
				this.newDepartment.municipality_id = ''
			}
		},

		// Create department
		createDepartment() {
			restApi
				.post(`/departments`, this.newDepartment)
				.then(() => {
					this.$swal.fire({
						icon: 'success',
						title: 'Department successfully created!',
						timer: 3000,
					})
					this.$createModal.hide()
					this.getTableData()
					this.newDepartment = {
						name: '',
						municipality_id: '',
						type: 'Police department',
						website: '',
						first_name: '',
						last_name: '',
						email: '',
						phone: '',
					}
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},
	},
}
</script>

<style lang="scss">
.search-item {
	display: flex;
	justify-content: center;
	cursor: pointer;
	border-bottom: 1px solid whitesmoke;
	&:hover {
		background-color: whitesmoke;
		border-radius: 0.5rem;
	}
}
</style>
